/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/prefer-stateless-function */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks';
import LayerToggle, {
  LayerToggleProps,
} from '../../../common-components/layer-toggle/layer-toggle';
import { setFocusRegionActive } from '../../../state/focus-region/focus-region.slice';
import {
  EMenuIcons,
  EMenuItems,
  MenuIcons,
  setSelectedOption,
} from '../../menu.slice';

interface SideMenuItemProps {
  itemName: string;
  iconName: EMenuIcons;
  menuOpen: boolean;
  selected: boolean;
  onClick: (() => void) | undefined;
  layerToggleProps: LayerToggleProps;
  itemDisabled?: boolean;
  featureBadgeContent?: string | null;
  iconColor?: string;
  focusRegionActive?: boolean;
}

function SideMenuItem(props: SideMenuItemProps) {
  // Items listed here will render the featureBadgeContent badge when provided
  const betaItems = new Set([EMenuItems.MY_FLEET, EMenuItems.IRAN_CONFLICT]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    itemName,
    iconName,
    menuOpen,
    onClick,
    selected,
    layerToggleProps,
    itemDisabled,
    featureBadgeContent,
    iconColor,
    focusRegionActive,
  } = props;

  const Icon = MenuIcons[iconName];

  const generateTooltip = () => {
    if (focusRegionActive && itemName !== EMenuItems.IRAN_CONFLICT) {
      return 'Focus Region mode is active — deselect to restore normal view.';
    }
    if (itemDisabled) {
      return 'You require a NorthStandard account to access these features.';
    }
    if (!menuOpen) {
      return itemName;
    }
    return '';
  };

  const resolvedIconSx = {
    marginInlineStart: menuOpen ? '1rem' : '0rem',
    ...(!itemDisabled && iconColor ? { color: `${iconColor}.main` } : {}),
  };

  return (
    <Tooltip title={generateTooltip()} placement="right">
      <Box sx={{ position: 'relative', paddingInline: '8px' }}>
        <Button
          title={itemName}
          data-testid={`side-menu-item-${itemName
            .toLowerCase()
            .replace(/\s+/g, '-')}`}
          className="side-menu-item"
          sx={{
            padding: '0.8rem 0',
            width: '100%',
            minWidth: 0,
            justifyContent: menuOpen ? 'left' : 'center',
            color: 'secondary.main',
            '&:hover': {
              backgroundColor: 'secondary.dark',
            },
            backgroundColor: selected ? 'secondary.dark' : 'inherit',
          }}
          onClick={onClick}
          disabled={itemDisabled}
        >
          {typeof Icon === 'string' ? (
            <img
              src={Icon}
              alt="icon"
              style={{
                height: '1.5rem',
                width: '1.5rem',
                marginInlineStart: menuOpen ? '1rem' : '0rem',
                // Provided SVG Icons weren't playing nice with the theme
                // We set them to black and use a CSS filter to change the color
                // https://stackoverflow.com/questions/22252472/how-can-i-change-the-color-of-an-svg-element
                // https://codepen.io/sosuke/pen/Pjoqqp
                filter: itemDisabled
                  ? 'invert(81%) sepia(5%) saturate(756%) hue-rotate(181deg) brightness(90%) contrast(81%)'
                  : 'invert(83%) sepia(36%) saturate(787%) hue-rotate(78deg) brightness(100%) contrast(97%)',
              }}
            />
          ) : (
            <Icon sx={resolvedIconSx} color="inherit" />
          )}
          {menuOpen && (
            <Typography className="menu-item" variant="body2">
              {itemName}
              {betaItems.has(itemName as EMenuItems) && featureBadgeContent && (
                <Badge
                  color="default"
                  badgeContent={featureBadgeContent}
                  sx={{
                    '& .MuiBadge-badge': {
                      right: -20,
                      top: -7,
                      fontSize: '0.7em',
                    },
                    '.MuiButtonBase-root:hover & .MuiBadge-badge': {
                      color: 'text.primary',
                      borderColor: 'text.primary',
                    },
                  }}
                  data-testid="side-menu-beta-badge"
                />
              )}
            </Typography>
          )}
        </Button>
        {menuOpen &&
          focusRegionActive &&
          itemName === EMenuItems.IRAN_CONFLICT && (
            /* Exit icon replaces the eye toggle when Focus Region mode is active */
            <Box
              data-testid="side-menu-item-toggle"
              sx={{ position: 'absolute', right: '1.5rem', top: '0.9rem' }}
            >
              <Tooltip title="Exit Focus Region Mode" placement="left">
                <IconButton
                  data-testid="focus-region-exit-button"
                  size="small"
                  aria-label="Exit Focus Region Mode"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(setFocusRegionActive(false));
                    dispatch(setSelectedOption(EMenuItems.NOTHING_SELECTED));
                    navigate('/');
                  }}
                  sx={{ color: 'secondary.main', padding: 0 }}
                >
                  <PowerSettingsNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        {menuOpen &&
          layerToggleProps &&
          layerToggleProps.layers &&
          !(focusRegionActive && itemName === EMenuItems.IRAN_CONFLICT) && (
            <Box
              data-testid="side-menu-item-toggle"
              sx={{ position: 'absolute', right: '1.5rem', top: '0.9rem' }}
            >
              {!layerToggleProps.disabled && (
                <LayerToggle
                  isSideMenuToggle
                  label={itemName}
                  {...layerToggleProps}
                  sx={{ padding: '0' }}
                />
              )}
            </Box>
          )}
      </Box>
    </Tooltip>
  );
}

SideMenuItem.defaultProps = {
  itemDisabled: false,
  featureBadgeContent: null,
  iconColor: undefined,
  focusRegionActive: false,
};

export default SideMenuItem;
